import React, { useState, useEffect } from "react";

import {
  Grid,
  Typography,
  Card,
  TextField,
  Box,
  CircularProgress,
  Divider,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
} from "@material-ui/core";
import SaveIcon from "@material-ui/icons/Save";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import NavigateNextIcon from "@material-ui/icons/NavigateNext";
import {
  fetchLabAPI,
  updateSelectedIdea,
  updateLabIdeas,
  cloneLabAPI,
  updateLabStatus,
} from "views/APIs/APIUtility";

// Styles and Assets
import styles from "assets/jss/material-dashboard-pro-react/views/dashboardStyle.js";
import { quCreateColor } from "assets/jss/material-dashboard-pro-react";
// Material-UI Styles and Connectors
import { makeStyles, withStyles } from "@material-ui/core/styles";

import MDEditor from "@uiw/react-md-editor";

// Material-UI Icons
import {
  Adjust,
  Assistant,
  BubbleChart,
  Category,
  CloudUpload,
  FileCopy,
} from "@material-ui/icons";

// Contexts
import NotificationContext from "views/Components/Context.js";

import Button from "components/CustomButtons/Button";
import Alert from "views/Components/Alert";
import {
  generateIdeasForConceptLabRefactored,
  generateRequirementsForConceptLabRefactored,
} from "../../APIs/LabsUtility";
import MarkdownEditorWithPreview from "../../../components/MarkdownEditor/MarkdownEditorWithPreview";

const useStyles = makeStyles((theme) => ({
  ...styles,
  gridItem: {
    // Full width on extra-small devices (mobile)
    flexBasis: "100%",
    maxWidth: "100%",
    [theme.breakpoints.up("md")]: {
      // On medium and larger screens, each item takes 20% of the width
      flexBasis: "20%",
      maxWidth: "20%",
    },
  },
  dropzone: {
    minHeight: "100px",
    backgroundColor: "#f0f0f0",
    borderColor: quCreateColor,
    border: "2px dashed",
    borderRadius: "5px",
    padding: "20px",
    textAlign: "center",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  previewText: {
    color: "#007BFF", // Change the text color
  },
  dropzoneText: {
    color: "#888888", // Grey color for instructions
  },
  resourceTile: {
    height: "100%", // Ensure all tiles have the same height
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between", // To ensure the content inside each tile is spaced well
    alignItems: "center",
    textAlign: "center",
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[3],
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    "&:hover": {
      boxShadow: theme.shadows[5],
      cursor: "pointer",
    },
  },
  dialogTitle: {
    fontWeight: "600",
    fontSize: "1.25rem",
    textAlign: "left",
    marginBottom: theme.spacing(3),
  },
}));

const LabIdea = ({ labID, setActiveStep }) => {
  const classes = useStyles();
  const { showNotification } = React.useContext(NotificationContext);
  const [lab, setLab] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [ideaTemplates, setIdeaTemplates] = useState([]);
  const [loading, setLoading] = useState({ isLoading: false, message: "" });
  const [changesMade, setChangesMade] = useState(false);
  const [activeTile, setActiveTile] = useState("Custom");
  const [showAlert, setShowAlert] = useState(false);
  const [reviewDialog, setReviewDialog] = useState(false);
  const [cloneConfirmDialog, setCloneConfirmDialog] = useState(false);
  const icons = [Adjust, Assistant, BubbleChart, Category, BubbleChart];

  useEffect(() => {
    async function fetchLabDetails() {
      try {
        setLoading({ isLoading: true, message: "Fetching lab details..." });
        const data = await fetchLabAPI(labID);
        setLab(data);
        setIdeaTemplates(data.lab_ideas);
        setActiveTile(data.selected_idea.name);
        setSelectedTemplate(data.selected_idea || data.lab_ideas[0]);
        // handleSetFileResources(data.raw_resources);
        setLoading({ isLoading: false, message: "" }); // Ensure loading state is reset
      } catch (error) {
        console.error("Error in fetchLabDetails:", error);
        showNotification("Error fetching lab details", "danger");
      } finally {
      }
    }

    fetchLabDetails();
  }, [labID]);

  const _handleTemplateSelect = (template) => {
    setActiveTile(template.name);
    setSelectedTemplate(template);
  };

  const _capitalize = (text) => {
    const lowerText = text.toLowerCase();
    return lowerText.charAt(0).toUpperCase() + lowerText.slice(1);
  };

  // Function to update the template content in newWritingDetails
  const _handleTemplateContentChange = (value) => {
    // Also update the selectedTemplate state to reflect changes immediately
    setSelectedTemplate({ ...selectedTemplate, description: value });
    const updatedTemplates = ideaTemplates.map((t) =>
      t.name === selectedTemplate.name ? { ...t, description: value } : t
    );
    setIdeaTemplates(updatedTemplates);
  };

  const handleUpdateSelectedIdea = async () => {
    try {
      const formData = new FormData();
      formData.append("lab_id", labID);
      const selectedIndex = ideaTemplates.findIndex(
        (template) => template.name === selectedTemplate.name
      );
      formData.append("index", parseInt(selectedIndex));

      await updateSelectedIdea(formData);
    } catch (error) {
      throw error;
    }
  };

  const handleNext = async () => {
    if (lab.status != "Idea Selection") {
      setShowAlert(true);
    } else {
      await handleSave();
      setReviewDialog(true);
    }
  };

  const submitHandler = async () => {
    try {
      handleReviewDialogClose();
      setLoading({
        isLoading: true,
        message: "Generating Requirements...",
      });
      await handleUpdateSelectedIdea();
      const formData = new FormData();
      formData.append("lab_id", labID);
      //   formData.append("use_metaprompt", true);
      //   await generateNotebookSpecificationsAPI(formData);
      await generateRequirementsForConceptLabRefactored(formData);
      window.location.reload();
    } catch (error) {
      console.error("Error in handleNext:", error);
      showNotification("Error generating Requirements", "danger");
    } finally {
      setLoading({ isLoading: false, message: "" });
    }
  };

  const handleConfirm = () => {
    setShowAlert(false); // Close the alert
    setReviewDialog(true);
  };

  const handleCancel = () => {
    setShowAlert(false); // Close the alert
  };

  const handleBack = () => {
    setActiveStep(1);
  };

  const handleSave = async () => {
    if (!selectedTemplate) {
      showNotification("No template selected to save", "warning");
      return;
    }

    try {
      setLoading({ isLoading: true, message: "Saving Ideas..." });

      // Update the selected template content inside ideaTemplates
      const updatedTemplates = ideaTemplates.map((t) =>
        t.name === selectedTemplate.name
          ? { ...t, description: selectedTemplate.description } // Use selectedTemplate.value
          : t
      );

      setIdeaTemplates(updatedTemplates); // Update state

      const formData = new FormData();
      formData.append("lab_id", labID);
      formData.append("lab_ideas", JSON.stringify(updatedTemplates));
      await updateLabIdeas(formData);
      showNotification("Lab Ideas saved successfully!", "success");
    } catch (error) {
      console.error("Error saving template:", error);
      showNotification("Error saving template", "danger");
    } finally {
      setLoading({ isLoading: false, message: "" });
    }
  };

  const handleReviewDialogClose = () => {
    setReviewDialog(false);
  };

  const handleCloneLabClick = (e, template) => {
    e.stopPropagation(); // Prevent tile selection when clicking clone
    setActiveTile(template.name);
    setSelectedTemplate(template);
    setCloneConfirmDialog(true);
  };

  const handleCloneLab = async () => {
    setCloneConfirmDialog(false);
    try {
      setLoading({ isLoading: true, message: "Cloning Lab..." });
      const formData = new FormData();
      formData.append("lab_id", labID);
      formData.append("name", selectedTemplate.name);
      const data = await cloneLabAPI(formData);

      showNotification("Lab cloned successfully!", "success");

      // set selected idea to the selectedTemplate in the cloned lab
      const formData2 = new FormData();
      formData2.append("lab_id", data._id);
      const selectedIndex = ideaTemplates.findIndex(
        (template) => template.name === selectedTemplate.name
      );
      formData2.append("index", parseInt(selectedIndex));
      await updateSelectedIdea(formData2);
      showNotification("Selected idea set successfully!", "success");

      const formData3 = new FormData();
      formData3.append("lab_id", data._id);
      formData3.append("lab_design_status", "Idea Selection");
      await updateLabStatus(formData3);

      showNotification("Lab status updated successfully!", "success");
      // Open cloned lab in new tab
      window.open(`/create/labdesign/lab/${data._id}`, "_blank");
    } catch (error) {
      console.error("Error cloning lab:", error);
      showNotification("Error cloning lab", "danger");
    } finally {
      setLoading({ isLoading: false, message: "" });
    }
  };

  const handleCloneCancel = () => {
    setCloneConfirmDialog(false);
  };

  return (
    <div>
      {/* Clone Confirmation Dialog */}
      <Dialog
        open={cloneConfirmDialog}
        onClose={handleCloneCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h5">Clone Lab</Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            This lab will be cloned with the name: <strong>{selectedTemplate?.name}</strong>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Box width="100%" display="flex" justifyContent="space-between" p={2}>
            <Button onClick={handleCloneCancel} color="secondary">
              Cancel
            </Button>
            <Button
              onClick={handleCloneLab}
              color="quCreateColor"
              variant="contained"
            >
              Clone
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Review Dialog */}
      <Dialog
        open={reviewDialog}
        onClose={handleReviewDialogClose}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle id="alert-dialog-title">
          <Typography variant="h4">Review</Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Box data-color-mode="light">
            <Typography variant="h5" gutterBottom>
              The following idea will be used as the basis for generating
              Notebook specifications.
            </Typography>
            {selectedTemplate && (
              <Box mt={2}>
                <Typography
                  variant="h6"
                  style={{ textTransform: "none" }}
                  gutterBottom
                >
                  {selectedTemplate.name}
                </Typography>
                <Box mt={1}>
                  <MarkdownEditorWithPreview
                    disabled={true}
                    value={selectedTemplate.description}
                    height={400}
                  />
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Box width="100%" display="flex" justifyContent="space-between" p={2}>
            <Button onClick={handleReviewDialogClose} color="secondary">
              Close
            </Button>
            <Button
              onClick={submitHandler}
              color="quCreateColor"
              variant="contained"
            >
              Confirm
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {showAlert && (
        <Alert
          title="Are you sure?"
          warning={true}
          showCancel={true}
          cancelBtnText="No"
          confirmBtnText="Yes"
          onCancel={handleCancel}
          onConfirm={handleConfirm}
        >
          Proceeding to the next step will reset your current progress in
          further steps. Your existing work will be overwritten, and new content
          will be created. Are you sure you want to continue?
        </Alert>
      )}
      {loading.isLoading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="60vh"
        >
          <CircularProgress className={classes.loaderClass} />
          <Typography
            variant="h6"
            style={{ marginLeft: 16, textTransform: "none" }}
          >
            {loading.message}
          </Typography>
        </Box>
      ) : (
        <>
          <Typography variant="h5" style={{}}>
            Idea Selection
          </Typography>
          <Typography variant="body1" style={{ marginBottom: "10px" }}>
            {_capitalize(
              `Choose one of the following ideas to start with or create your own.`
            )}
          </Typography>
          <Divider style={{ marginBottom: "20px" }} />

          {/* Grid for Template Tiles */}
          <Grid container spacing={2} justifyContent="center">
            {ideaTemplates.map((template, index) => (
              <Grid item className={classes.gridItem} key={index}>
                <Box
                  className={classes.resourceTile}
                  onClick={() => _handleTemplateSelect(template)}
                  style={{
                    backgroundColor:
                      template.name === activeTile
                        ? quCreateColor
                        : "transparent",
                    color:
                      template.name === activeTile ? "#fff" : quCreateColor,
                    position: "relative",
                  }}
                >
                  {/* Clone button - top right */}
                  <Box
                    style={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      zIndex: 2,
                      cursor: "pointer",
                    }}
                    onClick={(e) => handleCloneLabClick(e, template)}
                    title="Clone Lab"
                  >
                    <FileCopy
                      style={{
                        fontSize: 18,
                        color: template.name === activeTile ? "#fff" : quCreateColor,
                      }}
                    />
                  </Box>

                  {/* Icon and Heading */}
                  <Box mb={2}>
                    {icons[index] &&
                      React.createElement(icons[index], { fontSize: "large" })}
                  </Box>
                  <Typography variant="body1">{template.name}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          {/* Markdown Editor for Template Editing */}
          <Grid
            container
            spacing={3}
            alignItems="flex-start"
            style={{ marginTop: "20px" }}
          >
            <Grid item xs={12} style={{ flexGrow: 1 }}>
              <div data-color-mode="light">
                <MarkdownEditorWithPreview
                  value={selectedTemplate?.description || ""}
                  onChange={_handleTemplateContentChange}
                  height={350}
                />
              </div>
            </Grid>
          </Grid>

          <Grid container style={{ marginTop: "10px" }}>
            <Grid item xs={12}>
              <Button
                color="quCreateColor"
                style={{ float: "right", fontWeight: "bold" }}
                endIcon={<NavigateNextIcon />}
                onClick={handleNext}
              >
                Generate Requirements
              </Button>
              <Button
                color="quCreateColor"
                style={{ float: "left", fontWeight: "bold" }}
                startIcon={<ArrowBackIcon />}
                onClick={handleBack}
              >
                Back
              </Button>
              <Button
                color="quCreateColor"
                style={{ float: "right", fontWeight: "bold" }}
                startIcon={<SaveIcon />}
                onClick={handleSave}
              >
                Save
              </Button>
            </Grid>
          </Grid>
        </>
      )}
    </div>
  );
};

export default LabIdea;
